// Copyright (c) 2026 CTO's Club LLC. All rights reserved.
// Author: Alex Baretta (alex@baretta.com).

import { isAdminRoleAssignment } from '@pillar-portal/api-types';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CloverOAuthConfirmationDialog } from '@/components/clover/CloverOAuthConfirmationDialog';
import {
    buildCloverOAuthLaunchPath,
    parseCloverOAuthLaunchContext,
    type CloverOAuthLaunchContext,
} from '@/lib/clover/oauth-launch-context';
import { sessionSensitiveAuthApi } from '@/lib/sessionSensitiveAuthApi';
import { getActiveRoleAssignmentOperationalResponse } from '@/lib/active-role-assignment';

function buildAuthorizationLaunchUrl(
    context: CloverOAuthLaunchContext,
    organizationUuid: string
): string {
    const searchParams = new URLSearchParams({
        organization_uuid: organizationUuid,
        merchant_id: context.merchantId,
        client_id: context.clientId,
    });
    return `/api/clover/oauth/authorize/launch?${searchParams.toString()}`;
}

export default function CloverOAuthLaunchPage() {
    const [searchParams] = useSearchParams();
    const context = useMemo(() => parseCloverOAuthLaunchContext(searchParams), [searchParams]);
    const [confirmationOpen, setConfirmationOpen] = useState(true);
    const activeRoleAssignmentQuery = useQuery({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => await sessionSensitiveAuthApi.getActiveRoleAssignment(),
        retry: false,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: false,
    });
    const activeRoleAssignmentOperationalResponse = getActiveRoleAssignmentOperationalResponse(
        activeRoleAssignmentQuery.data
    );
    const canAuthorizeClover = isAdminRoleAssignment(activeRoleAssignmentOperationalResponse);
    const organizationUuid =
        activeRoleAssignmentOperationalResponse?.organization_is_merchant === true
            ? activeRoleAssignmentOperationalResponse.organization_uuid
            : null;

    if (context === null) {
        return (
            <main className="flex min-h-screen items-center justify-center p-4">
                <Card className="w-full max-w-lg">
                    <CardHeader>
                        <CardTitle>Invalid Clover launch</CardTitle>
                        <CardDescription>
                            The Clover launch did not include the required merchant context.
                        </CardDescription>
                    </CardHeader>
                </Card>
            </main>
        );
    }

    return (
        <main className="flex min-h-screen items-center justify-center p-4">
            <Card className="w-full max-w-lg">
                <CardHeader>
                    <CardTitle>Connect Clover</CardTitle>
                    <CardDescription>
                        Confirm the Merchant Organization of your active role assignment to connect
                        this Clover account.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {activeRoleAssignmentQuery.isError && (
                        <Alert variant="destructive">
                            <AlertDescription>
                                Unable to determine whether this role can connect Clover.
                            </AlertDescription>
                        </Alert>
                    )}
                    {activeRoleAssignmentQuery.isSuccess && !canAuthorizeClover && (
                        <Alert variant="destructive">
                            <AlertDescription>
                                An organizational administrator or higher role is required to
                                connect Clover.
                            </AlertDescription>
                        </Alert>
                    )}
                    {canAuthorizeClover && organizationUuid === null && (
                        <Alert>
                            <AlertDescription>
                                Select an active Merchant administrator or Principal role assignment
                                to connect Clover here. Internal administrators can connect a
                                Merchant from its Organization Details page.
                            </AlertDescription>
                        </Alert>
                    )}
                    {canAuthorizeClover && organizationUuid !== null && (
                        <CloverOAuthConfirmationDialog
                            open={confirmationOpen}
                            onOpenChange={setConfirmationOpen}
                            organizationUuid={organizationUuid}
                            organizationName={
                                activeRoleAssignmentOperationalResponse?.organization_name ?? null
                            }
                            authorizationPath={buildAuthorizationLaunchUrl(
                                context,
                                organizationUuid
                            )}
                            openInNewWindow={false}
                        />
                    )}
                    <Button
                        type="button"
                        className="w-full"
                        disabled={!canAuthorizeClover || organizationUuid === null}
                        onClick={() => setConfirmationOpen(true)}
                    >
                        Connect Clover
                    </Button>
                    <a
                        className="text-sm text-muted-foreground underline"
                        href={buildCloverOAuthLaunchPath(context)}
                    >
                        Restart this Clover launch
                    </a>
                </CardContent>
            </Card>
        </main>
    );
}
